import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6519d80c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "picker-wrap" }
const _hoisted_2 = { class: "picker-toolbar" }
const _hoisted_3 = { class: "picker-tip" }
const _hoisted_4 = { class: "picker-count" }

import { computed, onMounted, reactive, ref, watch } from 'vue';
import {
  BaseButton,
  BaseCheckbox,
  BaseDialog,
  BaseInput,
  BasePagination,
  BaseTable,
  BaseTableProps,
  useTableColumn,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';
import { getIndustryPriceTree, saveScenes } from '../data-source/apis';
import type { ScenePriceSceneItem } from '../data-source/types';
import {
  formatPriceInputText,
  formatPriceRange,
  getEffectiveIndustryRange,
  getPlatformRangeWarning,
  hasRangeError,
  isOutOfPlatformRange,
  validatePriceRange
} from '../utils/price-range';
import { MAX_PRICE, MIN_PRICE, PRICE_DECIMALS } from '../constants';

type PickerRow = ScenePriceSceneItem & {
  minText: string;
  maxText: string;
  minError: string;
  maxError: string;
  minWarn: string;
  maxWarn: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'scene-price-scene-picker-modal',
  props: {
    remove: { type: Function, default() {
      return () => {
        // auto-injected by useCreateModal
      };
    } },
    industryId: { default: '' },
    scenes: { default: () => [] }
  },
  emits: ['confirm'],
  setup(__props: any, { emit: __emit }) {

const emits = __emit;

const props = __props;

const loading = ref(false);
const saving = ref(false);
const keyword = ref('');
const currentPage = ref(1);
const pageSize = ref(15);
const pickerRows = ref<PickerRow[]>([]);
const selectedIds = reactive(new Set<string>());

const toPickerRow = (scene: ScenePriceSceneItem): PickerRow => {
  const platform: [number, number] = [
    scene.platformPriceLower,
    scene.platformPriceUpper
  ];
  const effective = getEffectiveIndustryRange(
    platform,
    scene.industryPriceLower,
    scene.industryPriceUpper
  );
  return {
    ...scene,
    minText: formatPriceInputText(effective[0]),
    maxText: formatPriceInputText(effective[1]),
    minError: '',
    maxError: '',
    minWarn: '',
    maxWarn: ''
  };
};

/** 合并接口数据与页面未保存的本地修改 */
const mergeScenes = (
  apiScenes: ScenePriceSceneItem[],
  localScenes: ScenePriceSceneItem[]
) => {
  const localMap = new Map(localScenes.map(item => [item.id, item]));
  return apiScenes.map(apiScene => {
    const local = localMap.get(apiScene.id);
    if (!local) {
      return apiScene;
    }
    return {
      ...apiScene,
      enabled: local.enabled,
      industryPriceLower: local.industryPriceLower,
      industryPriceUpper: local.industryPriceUpper
    };
  });
};

const syncSelectedIds = (scenes: ScenePriceSceneItem[]) => {
  selectedIds.clear();
  scenes
    .filter(item => item.enabled)
    .forEach(item => {
      selectedIds.add(item.id);
    });
};

const fetchScenes = async () => {
  loading.value = true;
  try {
    const industryId =
      props.industryId !== '' && props.industryId != null
        ? Number(props.industryId)
        : undefined;
    const apiScenes = await getIndustryPriceTree(industryId);
    const mergedScenes = mergeScenes(apiScenes, props.scenes);
    pickerRows.value = mergedScenes.map(toPickerRow);
    syncSelectedIds(mergedScenes);
  } catch (error) {
    console.error('获取开通场景列表失败:', error);
    XrkMessage.error('获取开通场景列表失败');
  } finally {
    loading.value = false;
  }
};

const filteredScenes = computed(() => {
  const q = keyword.value.trim();
  if (!q) {
    return pickerRows.value;
  }
  return pickerRows.value.filter(item => item.scenePath.includes(q));
});

/** 当前页数据 */
const pagedScenes = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return filteredScenes.value.slice(start, start + pageSize.value);
});

/** 分页切换（BasePagination 通过事件回传页码，而非 v-model） */
const onPageChange = (payload: { currentPage?: number; pageSize?: number }) => {
  if (payload.pageSize != null) {
    pageSize.value = payload.pageSize;
  }
  if (payload.currentPage != null) {
    currentPage.value = payload.currentPage;
  }
};

/** 搜索条件变化时回到第一页 */
watch(keyword, () => {
  currentPage.value = 1;
});

const validateRow = (row: PickerRow, required: boolean) => {
  const platform: [number, number] = [
    row.platformPriceLower,
    row.platformPriceUpper
  ];
  const err = validatePriceRange(row.minText, row.maxText, platform, required);
  row.minError = err.min;
  row.maxError = err.max;

  if (!required || hasRangeError(err)) {
    row.minWarn = '';
    row.maxWarn = '';
  } else {
    const warn = getPlatformRangeWarning(row.minText, row.maxText, platform);
    row.minWarn = warn.min;
    row.maxWarn = warn.max;
  }

  return !hasRangeError(err);
};

const columns: BaseTableProps['columns'] = [
  useTableColumn({
    label: '',
    prop: 'checked',
    width: 60,
    render: ({ row }) => (
      <BaseCheckbox
        modelValue={selectedIds.has(row.id)}
        onUpdate:modelValue={(checked: boolean) => onToggle(row.id, checked)}
      />
    )
  }),
  useTableColumn({
    label: '场景路径',
    prop: 'scenePath',
    minWidth: 320
  }),
  useTableColumn({
    label: '平台价格区间',
    prop: 'platformPrice',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（平台价格区间）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <span>
        {formatPriceRange(row.platformPriceLower, row.platformPriceUpper)}
      </span>
    )
  }),
  useTableColumn({
    prop: 'industryPriceLower',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（工业价下限）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <div>
        <BaseInput
          type="number"
          min={MIN_PRICE}
          max={MAX_PRICE}
          decimals={PRICE_DECIMALS}
          placeholder="下限"
          v-model={row.minText}
          onInput={() => validateRow(row, selectedIds.has(row.id))}
        />
        {row.minError ? (
          <div class="field-error">{row.minError}</div>
        ) : row.minWarn ? (
          <div class="field-warn">{row.minWarn}</div>
        ) : null}
      </div>
    )
  }),
  useTableColumn({
    prop: 'industryPriceUpper',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（工业价上限）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <div>
        <BaseInput
          type="number"
          min={MIN_PRICE}
          max={MAX_PRICE}
          decimals={PRICE_DECIMALS}
          placeholder="上限"
          v-model={row.maxText}
          onInput={() => validateRow(row, selectedIds.has(row.id))}
        />
        {row.maxError ? (
          <div class="field-error">{row.maxError}</div>
        ) : row.maxWarn ? (
          <div class="field-warn">{row.maxWarn}</div>
        ) : null}
      </div>
    )
  })
];

const onToggle = (id: string, checked: boolean) => {
  if (checked) {
    selectedIds.add(id);
  } else {
    selectedIds.delete(id);
  }
  const row = pickerRows.value.find(item => item.id === id);
  if (row) {
    validateRow(row, checked);
  }
};

const onSelectAll = (checked: boolean) => {
  filteredScenes.value.forEach(row => {
    if (checked) {
      selectedIds.add(row.id);
    } else {
      selectedIds.delete(row.id);
    }
    validateRow(row, checked);
  });
};

const onCancel = () => {
  props.remove();
};

const onConfirm = async () => {
  if (!props.industryId) {
    XrkMessage.warning('缺少工业信息，无法保存');
    return;
  }

  const selectedRows = pickerRows.value.filter(row => selectedIds.has(row.id));

  // 硬性校验：区间高低、必填等有问题则不能保存
  for (const row of selectedRows) {
    if (!validateRow(row, true)) {
      XrkMessage.warning('请检查已选场景的价格区间（须由低到高）');
      return;
    }
  }

  const hasOutOfPlatform = selectedRows.some(row =>
    isOutOfPlatformRange(row.minText, row.maxText, [
      row.platformPriceLower,
      row.platformPriceUpper
    ])
  );

  if (hasOutOfPlatform) {
    try {
      await XrkMessageBox.confirm(
        '您设置的价格已超出平台市场指导价格区间。平台市场指导价格仅供参考，不影响您自主定价。请确认是否继续按当前价格执行。',
        '提示',
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        }
      );
    } catch {
      return;
    }
  }

  const nextScenes = pickerRows.value.map(row => {
    const enabled = selectedIds.has(row.id);
    const isPlatformPrice =
      enabled &&
      Number(row.minText) === row.platformPriceLower &&
      Number(row.maxText) === row.platformPriceUpper;

    return {
      ...row,
      enabled,
      industryPriceLower: enabled
        ? isPlatformPrice
          ? null
          : Number(row.minText)
        : null,
      industryPriceUpper: enabled
        ? isPlatformPrice
          ? null
          : Number(row.maxText)
        : null
    };
  });

  saving.value = true;
  try {
    await saveScenes(props.industryId, nextScenes);
    emits('confirm', nextScenes);
    XrkMessage.success('已更新开通场景和工业价格');
    onCancel();
  } catch (error) {
    console.error('保存开通场景失败:', error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchScenes();
});

return (_ctx: any,_cache: any) => {
  const _directive_xrk_loading = _resolveDirective("xrk-loading")!

  return (_openBlock(), _createBlock(_unref(BaseDialog), {
    width: "980px",
    title: "选择开通场景",
    "model-value": true,
    "show-close": true,
    "close-on-press-escape": true,
    "close-on-click-modal": false,
    "show-footer": false
  }, {
    footer: _withCtx(() => [
      _createVNode(_unref(BaseButton), { onClick: onCancel }, {
        default: _withCtx(() => [
          _createTextVNode("取消")
        ]),
        _: 1
      }),
      _createVNode(_unref(BaseButton), {
        type: "primary",
        loading: saving.value,
        onClick: onConfirm
      }, {
        default: _withCtx(() => [
          _createTextVNode(" 确定 ")
        ]),
        _: 1
      }, 8, ["loading"])
    ]),
    default: _withCtx(() => [
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(BaseInput), {
            modelValue: keyword.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((keyword).value = $event)),
            clearable: "",
            placeholder: "搜索一级 / 二级 / 三级场景",
            style: {"flex":"1"}
          }, null, 8, ["modelValue"]),
          _createVNode(_unref(BaseButton), {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (onSelectAll(true)))
          }, {
            default: _withCtx(() => [
              _createTextVNode("全选")
            ]),
            _: 1
          }),
          _createVNode(_unref(BaseButton), {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (onSelectAll(false)))
          }, {
            default: _withCtx(() => [
              _createTextVNode("清空")
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createTextVNode(" 已选择 "),
          _createElementVNode("span", _hoisted_4, _toDisplayString(selectedIds.size), 1),
          _createTextVNode(" 个场景；工业价默认等于平台价，可超出平台区间（确定时需二次确认），数字区间须由低到高。 ")
        ]),
        _createVNode(_unref(BaseTable), {
          data: pagedScenes.value,
          columns: columns
        }, null, 8, ["data"]),
        _createVNode(_unref(BasePagination), {
          class: "picker-pagination",
          "current-page": currentPage.value,
          "page-size": pageSize.value,
          "page-sizes": [10, 15, 30, 50],
          layout: ['total', 'sizes', 'prev', 'pager', 'next', 'jumper'],
          total: filteredScenes.value.length,
          onCurrentChange: onPageChange,
          onSizeChange: onPageChange
        }, null, 8, ["current-page", "page-size", "total"])
      ])), [
        [_directive_xrk_loading, loading.value]
      ])
    ]),
    _: 1
  }))
}
}

})